import React, { FC, useState, useEffect, useMemo } from 'react';
import {
  Divider,
  Box,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Paper,
  DialogActions,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip
} from '@mui/material';
import LogoSpinner from 'src/components/LogoSpinner';
import { API, S3_CONFIG } from '../../../config/config';
import { useModalStore } from '../../../zustandStore/store.attributes';
import {
  ActionCell,
  DynamicTable,
  TableSearch
} from '../../../components/DynamicTable/DynamicTable';
// import fetchWithAuth from '../../../FetchWithAuth';
import CloseIcon from '@mui/icons-material/Close';
import { PanoramaFishEye, ZoomIn } from '@mui/icons-material';
import { useUpdateModalStore } from '../../../zustandStore/store.update';
import { Variant } from '../../../interface/interface';
import { Swiper, SwiperSlide } from 'swiper/react';
import { AsyncImage } from '../../../components/AsyncImage/AsyncImage';

import { PrizetBulkUpload } from '../../../components/PrizetBulkUpload';
import { useAuth } from 'src/hooks/useAuth';
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import PublishedStatusDialog from 'src/components/PublishedStatusDialog/PublishedStatusDialog';
import { PublishStatus } from 'src/components/PublishedStatusDialog/PublishedStatusDialog';
import {
  IServerFilter,
  ServerFilter
} from '../../../components/ServerFilter/ServerFilter';
import { toDateTime } from '../../../utils/payloadFormatter';
import { CreateProductVariantForm } from './CreateProductVariantForm';

interface RecentOrdersTableProps {
  className?: string;
}

export interface PrizeProducts {
  value: string; // This is crucial for the select dropdown
  label: string; // This is crucial for the select dropdown
  id: string;
  slug: string;
  productName: string;
  productDescription: string;
  brand: any;
  productLabel: any;
  active: boolean;
  galleries: any[];
  productAttributes: any[];
  published: boolean;
  tags: Tag[];
}

export interface Tag {
  id: string;
  tagName: string;
  icon: any;
}

type PageData = {
  content: any[];
  pageable: Pageable;
  totalPages: number;
  totalElements: number;
  last: boolean;
  size: number;
  number: number;
  sort: any;
  first: boolean;
  numberOfElements: number;
};

export interface Pageable {
  pageNumber: number;
  pageSize: number;
  sort: Sort;
  offset: number;
  paged: boolean;
  unpaged: boolean;
}

export interface Sort {
  empty: boolean;
  sorted: boolean;
  unsorted: boolean;
}

export interface PrizetImageAPI {
  image: string;
  placeholder: string;
  isThumbnail: boolean;
}

const ProductVariantsContent: FC<RecentOrdersTableProps> = () => {
  const { enhancedFetch } = useAuth();
  const [slug, setSlug] = useState('');
  const [productId, setProductId] = useState('');
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [published, setPublished] = useState<boolean>(false);
  const [salesPrice, setSalesPrice] = useState<number>(0);
  const [sku, setSku] = useState('');
  const [availableUnits, setAvailableUnits] = useState<number>(0);
  const [offers, setOffers] = useState([]);
  const [tags, setTags] = useState([]);
  const [attribute, setAttribute] = useState([]);
  const [attributeValues, setAttributeValues] = useState([]);
  const [brands, setBrands] = useState([]);
  const [tagValues, setTagValues] = useState([]);
  const [products, setProducts] = useState<any[]>([]);
  const [brandId, setBrandId] = useState();
  const [reload, setReload] = useState(false);
  const state = useModalStore((state) => state);
  const [currentProduct, setCurrentProduct] = useState<any>(null);
  const [editPublishedDialogOpen, setEditPublishedDialogOpen] =
    useState<boolean>(false);
  const [currentVariantForDetails, setCurrentVariantForDetails] =
    useState<Variant | null>(null);
  const [currentVariantForStatus, setCurrentVariantForStatus] =
    useState<Variant | null>(null);

  const { settings, actions } = useUpdateModalStore();
  const [states, setStates] = useState([]);
  const [productVariants, setProductVariants] = useState<Variant[]>([]);
  const [images, setImages] = useState<PrizetImageAPI[]>([]);
  const [sellers, setSellers] = useState([]);
  const [sortBy, SetSortBy] = useState<string>('id');
  const [sortAsc, setSortAsc] = useState<'asc' | 'desc'>('asc');
  const [showtag, setShowTag] = useState<boolean>(false);
  const [renderTags, setRenderTags] = useState<[]>([]);
  const [page, setPage] = useState<number>(0); // Typically, page numbering starts at 0
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [pageData, setPageData] = useState<PageData>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [isInSearchMode, setIsInSearchMode] = useState(false);
  const [originalPrice, setOriginalPrice] = useState<number>(0);
  const [additionalInfo, setAdditionalInfo] = useState<string[]>([]);
  const [buyingPrice, setBuyingPrice] = useState<number>(0);
  const [filterApplied, setFilterApplied] = useState<IServerFilter>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);

  const handleFormSubmit = async (formData) => {
    setLoading(true);

    delete formData['slug'];
    try {
      const response = await enhancedFetch(`${API.HOST}/api/v1/variants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          additionalInfo: (formData?.additionalInfo as string).split('\n'),
          tags: formData?.tags?.map((tag) => ({
            tagName: tag.label
          })),
          states: formData?.states?.map((state) => state.value)
        })
      });

      if (settings.isEdit) {
        actions.setModal({ modalOpen: false, isEdit: false });
      } else {
        state.actions.setModal({ modalOpen: false });
      }

      // Refresh the product variants list
      const fetchData = await enhancedFetch(
        `${API.HOST}/api/v1/variants/adminsearch?page=${page}&size=${rowsPerPage}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            page,
            size: rowsPerPage
          })
        }
      );

      if (
        fetchData &&
        fetchData.paginatedVariants &&
        Array.isArray(fetchData.paginatedVariants.content)
      ) {
        setProductVariants(fetchData.paginatedVariants.content);
        setPageData(fetchData.paginatedVariants);
      }

      setFormDialogOpen(false);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 1. Attributes fetch
    enhancedFetch(`${API.HOST}/api/v1/attributes`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((data) => {
        // Check if data is an array before mapping
        if (Array.isArray(data)) {
          setAttribute(
            data.map((attribute: any) => {
              return {
                value: attribute.id,
                label: attribute.name,
                attributeValues: attribute.attributeValues
              };
            })
          );
        } else {
          console.error(
            'Expected attributes data to be an array, got:',
            typeof data
          );
          setAttribute([]); // Set empty array as fallback
        }
      })
      .catch((error) => {
        console.error('Error fetching attributes:', error);
        setAttribute([]);
      });

    // 2. States fetch
    enhancedFetch(`${API.HOST}/api/v1/address/states`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((data) => {
        if (Array.isArray(data)) {
          setStates(
            data.map((attribute: any) => {
              return {
                value: attribute.id,
                name: attribute.name
              };
            })
          );
        } else {
          console.error(
            'Expected states data to be an array, got:',
            typeof data
          );
          setStates([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching states:', error);
        setStates([]);
      });

    // 3. Tags fetch
    enhancedFetch(`${API.HOST}/api/v1/tags`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((data) => {
        if (Array.isArray(data)) {
          setTags(
            data.map((attribute: any) => {
              return {
                id: attribute.id,
                tagName: attribute.tagName
              };
            })
          );
        } else {
          console.error('Expected tags data to be an array, got:', typeof data);
          setTags([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching tags:', error);
        setTags([]);
      });

    // 4. Products fetch
    enhancedFetch(`${API.HOST}/api/v1/products/all`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((data) => {
        if (Array.isArray(data)) {
          // If data is an array, use it directly
          setProducts(
            data.map((product: PrizeProducts) => {
              return {
                value: product.id,
                label: product.productName,
                slug: product.slug,
                id: product.id,
                productName: product.productName,
                productDescription: product.productDescription,
                brand: product.brand,
                productLabel: product.label,
                active: product.active,
                galleries: product.galleries,
                productAttributes: product.productAttributes,
                published: product.published,
                tags: product.tags
              };
            })
          );
        } else if (data && data.content && Array.isArray(data.content)) {
          // If data has content property that is an array
          setProducts(
            data.content.map((product: PrizeProducts) => {
              return {
                value: product.id,
                label: product.productName,
                slug: product.slug,
                id: product.id,
                productName: product.productName,
                productDescription: product.productDescription,
                brand: product.brand,
                productLabel: product.label,
                active: product.active,
                galleries: product.galleries,
                productAttributes: product.productAttributes,
                published: product.published,
                tags: product.tags
              };
            })
          );
        } else {
          console.error(
            'Expected products data to be an array or have a content array property:',
            typeof data
          );

          // Check if data has content property that might be an array
          if (data && typeof data === 'object' && data.content) {
            setProducts(
              data.content.map((product: PrizeProducts) => ({
                value: product.id,
                label: product.productName,
                slug: product.slug,
                id: product.id,
                productName: product.productName,
                productDescription: product.productDescription,
                brand: product.brand,
                productLabel: product.label,
                active: product.active,
                galleries: product.galleries,
                productAttributes: product.productAttributes,
                published: product.published,
                tags: product.tags
              }))
            );
          } else {
            setProducts([]);
          }
          setProducts([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching products:', error);
        setProducts([]);
      });

    // 5. Sellers fetch
    enhancedFetch(`${API.HOST}/api/v1/sellers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((data) => {
        if (Array.isArray(data)) {
          setSellers(
            data.map((seller) => ({
              value: seller.id,
              label: seller.name
            }))
          );
        } else {
          console.error(
            'Expected sellers data to be an array, got:',
            typeof data
          );
          setSellers([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching sellers:', error);
        setSellers([]);
      });

    // 6. Brands fetch
    enhancedFetch(`${API.HOST}/api/v1/brands`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((data) => {
        if (Array.isArray(data)) {
          setBrands(
            data.map((attribute: any) => {
              return {
                value: attribute.id,
                label: attribute.name
              };
            })
          );
        } else {
          console.error(
            'Expected brands data to be an array, got:',
            typeof data
          );
          setBrands([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching brands:', error);
        setBrands([]);
      });
  }, []);

  // reload
  // PAGINATED API CALL
  // useEffect that fetches products on page/rowsPerPage change
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        let endpoint;
        let requestBody = {};

        if (isInSearchMode && currentSearchQuery) {
          endpoint = `${API.HOST}/api/v1/variants/rawsearch`;
          requestBody = {
            query: currentSearchQuery,
            ...filterApplied
          };
        } else {
          endpoint = `${API.HOST}/api/v1/variants/adminsearch?page=${page}&size=${rowsPerPage}`;
          if (filterApplied) {
            requestBody = {
              ...filterApplied,
              page,
              size: rowsPerPage,
              query: currentSearchQuery
            };
          }
        }

        const response = await enhancedFetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        if (
          response &&
          response.paginatedVariants &&
          Array.isArray(response.paginatedVariants.content)
        ) {
          setProductVariants(response.paginatedVariants.content);
          setPageData(response.paginatedVariants);
        } else if (Array.isArray(response)) {
          setProductVariants(response);
          // Create mock pagination data
          setPageData({
            content: response,
            pageable: {
              pageNumber: page,
              pageSize: rowsPerPage,
              sort: { empty: true, sorted: false, unsorted: true },
              offset: page * rowsPerPage,
              paged: true,
              unpaged: false
            },
            totalElements: response.length,
            totalPages: Math.ceil(response.length / rowsPerPage),
            last: page >= Math.ceil(response.length / rowsPerPage) - 1,
            size: rowsPerPage,
            number: page,
            sort: { empty: true, sorted: false, unsorted: true },
            first: page === 0,
            numberOfElements: response.length
          });
        } else {
          console.error('Unexpected response format:', response);
          setProductVariants([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, rowsPerPage, isInSearchMode, currentSearchQuery]);

  useEffect(() => {
    if (!productVariants || productVariants.length === 0) return;

    // Create a new array instead of mutating state
    const sorted = [...productVariants].sort((a, b) => {
      // Simplified sorting logic
      const valA = a[sortBy];
      const valB = b[sortBy];

      if (sortAsc === 'asc') {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });

    // Update with sorted array
    setProductVariants(sorted);
  }, [sortBy, sortAsc]);

  // Function to handle the PUT request to update the published status
  const handleUpdatePublishedStatus = async (
    variantId: string,
    status: PublishStatus
  ) => {
    try {
      const response = await enhancedFetch(
        `${API.HOST}/api/v1/variants/${variantId}/update/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: status // Send the PublishStatus enum value directly
          })
        }
      );

      // Update the product variants in state, modifying the status property
      setProductVariants((prev) =>
        prev?.map((variant) =>
          variant.id === variantId
            ? {
                ...variant,
                status: status // Update the status property directly
              }
            : variant
        )
      );

      return response;
    } catch (error) {
      console.error('Error in handleUpdatePublishedStatus:', error);
      throw error;
    }
  };
  // Handler for search execution
  const handleSearch = async (query) => {
    if (!query.trim()) return;

    setIsSearching(true);
    try {
      setIsInSearchMode(true);
      setCurrentSearchQuery(query);
      setPage(0); // Always reset to first page on new search
    } finally {
      setIsSearching(false);
    }
  };

  // Handler for clearing search
  const handleClearSearch = () => {
    setIsSearching(true);
    try {
      setIsInSearchMode(false);
      setCurrentSearchQuery('');
      setPage(0); // Reset to first page when clearing search
    } finally {
      setIsSearching(false);
    }
  };

  const ProductVariantFormModal = CreateProductVariantForm({
    onSubmit: handleFormSubmit,
    data: {
      sellers,
      products,
      slug,
      productName,
      productDescription,
      brandId,
      published,
      images,
      tags,
      tagValues,
      attributeValues,
      brands,
      attribute,
      salesPrice,
      originalPrice,
      availableUnits,
      buyingPrice,
      states,
      additionalInfo,
      productId
    },
    onChange: async (data) => {},
    openModal: state.settings.modalOpen,
    editData: settings?.payload,
    editModal: settings.isEdit,
    onClose: () => {
      if (settings.isEdit) {
        actions.setModal({ modalOpen: false, isEdit: false });
      } else {
        state.actions.setModal({ modalOpen: false });
      }
    },
    title: settings.isEdit ? 'Edit Product Variant' : 'Add Product Variant',
    isLoading: loading,
    fullWidth: false,
    maxWidth: 'md'
  });

  const renderTagValues = () => {
    return (
      <div id={'view-tags'}>
        <Dialog
          maxWidth={'lg'}
          onClose={(e) => setCurrentProduct(null)}
          open={showtag}
          PaperProps={{ style: { zIndex: 1000 } }}
        >
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            Tags
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={(e) => {
              setShowTag(false);
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 350 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Value</TableCell>
                  </TableRow>
                </TableHead>
                {/* eslint-disable-next-line react/jsx-no-undef */}
                <TableBody>
                  {renderTags?.map((row: { tagName: string }, index) => (
                    <TableRow
                      key={index}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>{row.tagName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button disabled={false} autoFocus onClick={(e) => {}}>
              {<CircularProgress size={6} />}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  };

  return (
    <Card>
      {renderTagValues()}

      {/* Product Variant Form */}
      {ProductVariantFormModal}

      <Dialog maxWidth={'md'} fullWidth={true} open={state.settings.bulkUpload}>
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Bulk Upload
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={(e) =>
            state.actions.setModal({ modalOpen: false, bulkUpload: false })
          }
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <PrizetBulkUpload url={`${API.HOST}/api/v1/variants/bulk-upload`} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={false} autoFocus onClick={(e) => {}}>
            {<CircularProgress size={6} />}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        maxWidth={'md'}
        fullWidth={true}
        open={!!currentVariantForDetails}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          {currentVariantForDetails?.title || 'Product Variant Details'}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={(e) => setCurrentVariantForDetails(null)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Box sx={{ height: '100%', width: 400, padding: '2%' }}>
              <Swiper
                modules={[Navigation, Scrollbar, A11y]}
                navigation
                spaceBetween={50}
                slidesPerView={1}
                height={'100%'}
              >
                {currentVariantForDetails?.images?.map(
                  (item: any, idx: any) => (
                    <Box key={item + 'swiper' + idx}>
                      <SwiperSlide key={item + 'swiper'}>
                        <AsyncImage
                          isPopUp={true}
                          w={'100%' as any}
                          h={'50%' as any}
                          url={item.image}
                        />
                      </SwiperSlide>
                    </Box>
                  )
                )}
              </Swiper>
            </Box>

            <TableContainer>
              <Table sx={{ maxWidth: '100%' }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Attributes</TableCell>
                    <TableCell>Values</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentVariantForDetails?.variantAttributeValues
                    ?.sort((a, b) => (a.name > b.name ? 1 : -1))
                    ?.map((row, index) => (
                      <TableRow
                        key={row?.id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 }
                        }}
                      >
                        <TableCell key={`cell-name-${index}-${row.id}`}>
                          {row?.name}
                        </TableCell>
                        <TableCell key={`cell-value-${index}-${row.id}`}>
                          {row?.productAttributeValue}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={false} autoFocus onClick={(e) => {}}>
            {<CircularProgress size={6} />}
          </Button>
        </DialogActions>
      </Dialog>

      <Divider />

      {/* Add our custom search component above the DynamicTable */}

      {/* Filter Bar Integrated within DynamicTable */}
      <ServerFilter
        filtersAction={setFilterApplied}
        onQueryChange={(query) => {
          setCurrentSearchQuery(query);
        }}
        hideProductName={true}
        setLoader={setLoading}
        endpoint={`${API.HOST}/api/v1/variants/adminsearch`}
        onResultFetched={(data) => {
          setProductVariants(data.paginatedVariants.content);
          setPageData(data.paginatedVariants);
          setPage(data.paginatedVariants.pageable.pageNumber);
        }}
        products={productVariants?.map((item) => ({
          id: item.id,
          title: item.title
        }))}
      />

      <TableContainer>
        {loading ? (
          <Box
            sx={{
              height: '400px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <LogoSpinner />
          </Box>
        ) : (
          <DynamicTable
            showPagination={true}
            rowsPerPage={isInSearchMode ? pageData?.totalElements : rowsPerPage}
            page={page}
            totalPage={pageData?.totalElements}
            sortByField={sortBy}
            onSort={(asc: 'desc' | 'asc') => {
              SetSortBy(asc);
              setSortAsc(asc);
            }}
            sort={sortAsc}
            // add select fields to filter data by brand, vendor, product etc
            headers={[
              {
                headerName: 'Vendor',
                field: 'vendor'
              },
              {
                headerName: 'Products Name',
                field: 'productName'
              },
              {
                headerName: 'Colors',
                field: 'colors',
                renderCell: (value) => {
                  // ProductAttributeValues
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                      {value?.row.actions.variantAttributeValues
                        ?.filter((item) => item.name === 'Colors')
                        .map((colorItem, index) => (
                          <Chip
                            key={index}
                            label={colorItem.productAttributeValue}
                            sx={{
                              margin: '2px',
                              backgroundColor: 'gold',
                              color: 'black',
                              textTransform: 'capitalize'
                            }}
                          />
                        ))}
                    </Box>
                  );
                }
              },
              {
                headerName: 'Status',
                field: 'status',
                renderCell: ({ row }) => {
                  return (
                    <Chip
                      label={row.actions?.status}
                      onClick={(event: any) => {
                        event.stopPropagation();
                        event.preventDefault();
                        setCurrentVariantForStatus(row.actions);
                        setEditPublishedDialogOpen(true);
                      }}
                      sx={{
                        margin: '2px',
                        backgroundColor:
                          row.actions?.status === 'PUBLISHED' ? 'green' : 'red',
                        color:
                          row.actions?.status === 'PUBLISHED' ? 'red' : 'white',
                        textTransform: 'capitalize'
                      }}
                    />
                  );
                }
              },
              {
                headerName: 'Tags',
                field: 'tags',
                renderCell: (value) => {
                  return (
                    <Button
                      variant={'contained'}
                      type={'button'}
                      onClick={(e) => {
                        setShowTag(true);
                        setRenderTags(value.row.tags as []);
                      }}
                    >
                      View Tags
                    </Button>
                  );
                }
              },
              {
                headerName: 'Date Updated',
                field: 'updatedAt'
              },
              {
                headerName: 'Actions',
                field: 'actions',
                renderCell: (value) => {
                  return (
                    <ActionCell
                      deleteEndpoint={`${API.HOST}/api/v1/variants/${value.row.id}`}
                      editEndpoint={`${API.HOST}/api/v1/variants/${value.row.id}`}
                      payload={value.row.actions}
                      callback={() => setReload(!reload)}
                    >
                      <IconButton
                        onClick={() => {
                          setCurrentVariantForDetails(value.row.actions);
                          actions.setModal({
                            modalOpen: false,
                            isEdit: false
                          });
                        }}
                        color="inherit"
                        size="small"
                      >
                        <ZoomIn fontSize="small" />
                      </IconButton>
                    </ActionCell>
                  );
                }
              }
            ]}
            rows={productVariants?.map((item) => ({
              id: item.id,
              vendor: item.vendor.name,
              productName: item.title,
              brandName: item.product.brand.name,
              status: item.status,
              tags: item.tags,
              updatedAt: toDateTime(item.updatedAt),
              actions: item,
              images: item.images
            }))}
            handleChangePage={(event, newPage) => setPage(newPage)}
            handleChangeRowsPerPage={(event) => setRowsPerPage(event)}
          />
        )}
      </TableContainer>
      <PublishedStatusDialog
        open={editPublishedDialogOpen}
        onClose={() => setEditPublishedDialogOpen(false)}
        variant={currentVariantForStatus}
        onUpdate={handleUpdatePublishedStatus}
      />
    </Card>
  );
};

export default ProductVariantsContent;
